import { useEffect, useState } from "react";
import axios from "axios";
import UserAvatar from "./UserAvatar";
import { Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "invited_by_uuid";
const STORAGE_NAME_KEY = "invited_by_name";
const STORAGE_AVATAR_KEY = "invited_by_avatar";

/**
 * SoulCardInviteBanner — drives the Soul-Card-deeplinked invite flow.
 *
 * On every app mount:
 *   1. Read `?invited_by=<uuid>` from the URL.
 *   2. If present, persist to localStorage so the inviter survives the user's
 *      route through the auth pages (login/signup/onboarding).
 *   3. Strip the param from the address bar (cleaner URLs after landing).
 *   4. Look up the inviter's name + avatar via GET /api/auth/inviter/{uuid}
 *      and cache them so the signup page can render a "Brought in by X" badge
 *      without re-fetching.
 *
 * Render: a softly-animated banner that shows on landing/login/signup as long
 * as the invite is still pending. Dismissable but not hidden permanently —
 * the localStorage entry is the source of truth and only clears on successful
 * signup or explicit dismiss.
 */
const SoulCardInviteBanner = () => {
  const [inviter, setInviter] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // 1. URL param takes precedence — overwrites any stale stored value.
      let uuid = null;
      try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = (params.get("invited_by") || "").trim();
        if (fromUrl) {
          uuid = fromUrl;
          localStorage.setItem(STORAGE_KEY, fromUrl);
          // Strip ?invited_by from the URL so refreshes don't keep it in sight.
          params.delete("invited_by");
          const qs = params.toString();
          const cleanUrl =
            window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
          window.history.replaceState({}, "", cleanUrl);
        } else {
          uuid = (localStorage.getItem(STORAGE_KEY) || "").trim() || null;
        }
      } catch {
        uuid = null;
      }
      if (!uuid) return;

      // 2. Try cached inviter info first.
      const cachedName = localStorage.getItem(STORAGE_NAME_KEY);
      const cachedAvatar = localStorage.getItem(STORAGE_AVATAR_KEY);
      if (cachedName) {
        setInviter({ name: cachedName, avatar_url: cachedAvatar || null });
      }

      // 3. Refresh from backend (always — names/avatars can change).
      try {
        const res = await axios.get(
          `${API}/auth/inviter/${encodeURIComponent(uuid)}`,
        );
        if (cancelled) return;
        if (res.data?.name) {
          setInviter({
            name: res.data.name,
            avatar_url: res.data.avatar_url || null,
          });
          localStorage.setItem(STORAGE_NAME_KEY, res.data.name);
          if (res.data.avatar_url) {
            localStorage.setItem(STORAGE_AVATAR_KEY, res.data.avatar_url);
          } else {
            localStorage.removeItem(STORAGE_AVATAR_KEY);
          }
        } else {
          // Inviter UUID didn't resolve. Drop everything.
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_NAME_KEY);
          localStorage.removeItem(STORAGE_AVATAR_KEY);
        }
      } catch {
        /* network hiccup — keep cached badge if any */
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!inviter || hidden) return null;

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] px-3 py-2 rounded-full bg-background/90 backdrop-blur-md border border-violet-500/40 shadow-lg flex items-center gap-2 text-xs sm:text-sm max-w-[92vw]"
      role="status"
      data-testid="soul-card-invite-banner"
    >
      <Sparkles className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />
      <UserAvatar
        src={inviter.avatar_url}
        name={inviter.name}
        size={22}
      />
      <span className="text-foreground/90 truncate">
        Brought in by{" "}
        <span className="font-semibold text-primary">{inviter.name}</span>
      </span>
      <button
        type="button"
        aria-label="Dismiss invite badge"
        onClick={() => setHidden(true)}
        className="ml-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default SoulCardInviteBanner;
